/** Pure Observability display mapping — no I/O. D4H. */

import {
  alertLabelFa,
  alertSeverityTone,
  backupAgeTone,
  formatBackupAgeFa,
  killSwitchLabelFa,
  killSwitchTone,
  readinessLabelFa,
  readinessTone,
  type StatusTone,
} from "./overview-ops";
import { cmsStrings } from "../terminology";

export type AlertSortKey = "fired" | "alert" | "severity" | "status";
export type AggregateSortKey = "bucket" | "site" | "status" | "count";
export type DrillSortKey = "recorded" | "ok" | "duration";

export type StatusDisplay = { label: string; tone: StatusTone; detail?: string };

export type ObservabilityAlertRow = {
  id: string;
  firedAt: string;
  alert: string;
  severity: string;
  status: string;
  details: Record<string, unknown>;
};

export type ObservabilityAggregateListRow = {
  key: string;
  bucketUtc: string;
  siteCode: string;
  organizationId: string;
  httpStatus: string;
  sponsored: number;
  count: number;
};

export type ObservabilityDrillRow = {
  id: string;
  recordedAt: string;
  ok: boolean;
  kind: string;
  isolated: boolean;
  backupSha256: string | null;
  backupBasename: string | null;
  source: string;
  evidenceSummary: string;
  durationMs: number;
};

/** Prominent privacy copy — must stay visible on the page. */
export const OBSERVABILITY_PRIVACY_BANNER_FA = cmsStrings.observability.privacyBanner;

export const OBSERVABILITY_NO_LIVE_OPS_NOTE = cmsStrings.observability.noLiveOpsNote;

/** Operator-facing aggregate disclaimer (Dari only in UI — L3A). */
export const OBSERVABILITY_AGGREGATE_DISCLAIMER_UI_FA =
  cmsStrings.observability.aggregateDisclaimer;

const SECRETISH_KEY =
  /^(.*(_|-|\.)?)?(password|passwd|secret|token|cookie|authorization|auth|csrf|mfa|recovery|session|webhook|api[_-]?key|private[_-]?key|url|endpoint|destination|payload|raw|log|body|header|mac|email|user[_-]?agent|ip|imsi)s?$/i;

const SAFE_DETAIL_KEYS = new Set([
  "consecutive",
  "failedChecks",
  "ageSeconds",
  "thresholdSeconds",
  "usedPercent",
  "freeBytes",
  "streak",
  "enabledForSeconds",
  "daysSinceSuccess",
  "thresholdDays",
  "reason",
  "note",
  "check",
  "status",
  "ready",
  "count",
  "sites",
  "isolated",
]);

export function truncateSha256(value: string | null | undefined, head = 12): string {
  if (!value) return "—";
  if (value.length <= head + 1) return value;
  return `${value.slice(0, head)}…`;
}

export function formatBytesFa(value: number | null | undefined): string {
  if (value == null) return "—";
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  if (value < 1024 * 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  return `${(value / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export function checkLabelFa(check: string): string {
  if (check === "ok") return "سالم";
  if (check === "fail") return "ناموفق";
  if (check === "stale") return "کهنه";
  if (check === "missing") return "موجود نیست";
  return check;
}

export function backupAgeDisplay(seconds: number | null | undefined): StatusDisplay {
  if (seconds == null) {
    return {
      label: "در دسترس نیست",
      tone: "neutral",
      detail: "سن پشتیبان تا وجود یک snapshot معتبر در دسترس نیست",
    };
  }
  return {
    label: formatBackupAgeFa(seconds),
    tone: backupAgeTone(seconds),
  };
}

export function alertStatusDisplay(status: string): StatusDisplay {
  if (status === "firing") return { label: "ارسال‌شده", tone: "success" };
  if (status === "skipped_no_destination") {
    return {
      label: "رد شده — بدون مقصد",
      tone: "warning",
      detail: "تحویل هشدار رد شد چون مقصد وب‌هوک پیکربندی نشده است",
    };
  }
  if (status === "webhook_failed") return { label: "شکست وب‌هوک", tone: "danger" };
  if (status === "privacy_blocked") return { label: "مسدود حریم خصوصی", tone: "warning" };
  return { label: status, tone: "neutral" };
}

export function drillResultDisplay(ok: boolean): StatusDisplay {
  return ok
    ? { label: "موفق", tone: "success", detail: "تمرین ایزوله — پایگاه زنده تغییر نکرد" }
    : { label: "ناموفق", tone: "danger" };
}

export function webhookConfiguredDisplay(configured: boolean): StatusDisplay {
  return configured
    ? {
        label: "پیکربندی‌شده",
        tone: "success",
        detail: "فقط وضعیت — URL یا رمز مقصد نمایش داده نمی‌شود",
      }
    : {
        label: "خاموش",
        tone: "warning",
        detail: "بدون مقصد وب‌هوک، هشدار آزمایشی با وضعیت skipped_no_destination ثبت می‌شود",
      };
}

export function scrapeTokenDisplay(configured: boolean): StatusDisplay {
  return configured
    ? {
        label: "توکن فعال",
        tone: "info",
        detail: "فقط وضعیت پیکربندی — مقدار توکن هرگز در UI نیست",
      }
    : { label: "بدون توکن", tone: "neutral" };
}

/**
 * Safe operator-facing detail pairs — never render webhook URLs, tokens,
 * credentials, raw payloads, or secret-looking keys.
 */
export function safeAlertDetailEntries(
  details: Record<string, unknown> | null | undefined,
): Array<{ key: string; value: string }> {
  if (!details || typeof details !== "object") return [];
  const out: Array<{ key: string; value: string }> = [];
  for (const [key, raw] of Object.entries(details)) {
    if (SECRETISH_KEY.test(key)) continue;
    if (!SAFE_DETAIL_KEYS.has(key) && typeof raw === "string" && raw.length > 80) continue;
    if (!SAFE_DETAIL_KEYS.has(key) && typeof raw === "object" && raw !== null) continue;
    if (typeof raw === "string" || typeof raw === "number" || typeof raw === "boolean") {
      out.push({ key, value: String(raw) });
    } else if (raw === null) {
      out.push({ key, value: "—" });
    }
  }
  return out;
}

export function formatSafeAlertDetails(details: Record<string, unknown> | null | undefined): string {
  const entries = safeAlertDetailEntries(details);
  if (entries.length === 0) return "—";
  return entries.map((e) => `${e.key}=${e.value}`).join(" · ");
}

export { alertLabelFa, alertSeverityTone, readinessLabelFa, readinessTone, killSwitchLabelFa, killSwitchTone };

export function matchesAlertQuery(row: ObservabilityAlertRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [row.alert, alertLabelFa(row.alert), row.severity, row.status, row.id]
    .join(" ")
    .toLowerCase()
    .includes(q);
}

export function matchesAlertFilter(row: ObservabilityAlertRow, filter: string): boolean {
  if (filter === "all") return true;
  if (filter === "skipped") return row.status === "skipped_no_destination";
  if (filter === "firing") return row.status === "firing";
  if (filter === "failed") return row.status === "webhook_failed" || row.status === "privacy_blocked";
  return row.alert === filter;
}

export function filterAndSortAlerts(
  rows: ObservabilityAlertRow[],
  opts: { query: string; filter: string; sort: AlertSortKey },
): ObservabilityAlertRow[] {
  const filtered = rows.filter(
    (r) => matchesAlertQuery(r, opts.query) && matchesAlertFilter(r, opts.filter),
  );
  const sorted = [...filtered];
  sorted.sort((a, b) => {
    if (opts.sort === "alert") return a.alert.localeCompare(b.alert);
    if (opts.sort === "severity") return a.severity.localeCompare(b.severity);
    if (opts.sort === "status") return a.status.localeCompare(b.status);
    return b.firedAt.localeCompare(a.firedAt);
  });
  return sorted;
}

export function toAggregateListRow(row: {
  bucketUtc: string;
  siteCode: string;
  organizationId: string;
  httpStatus: string;
  sponsored: number;
  count: number;
}): ObservabilityAggregateListRow {
  return {
    key: `${row.bucketUtc}-${row.siteCode}-${row.httpStatus}-${row.sponsored}`,
    ...row,
  };
}

export function matchesAggregateQuery(row: ObservabilityAggregateListRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [row.bucketUtc, row.siteCode, row.httpStatus, row.organizationId, String(row.count)]
    .join(" ")
    .toLowerCase()
    .includes(q);
}

export function matchesAggregateFilter(row: ObservabilityAggregateListRow, filter: string): boolean {
  if (filter === "all") return true;
  if (filter === "5xx") return row.httpStatus === "5xx";
  if (filter === "sponsored") return row.sponsored === 1;
  return row.httpStatus === filter;
}

export function filterAndSortAggregates(
  rows: ObservabilityAggregateListRow[],
  opts: { query: string; filter: string; sort: AggregateSortKey },
): ObservabilityAggregateListRow[] {
  const filtered = rows.filter(
    (r) => matchesAggregateQuery(r, opts.query) && matchesAggregateFilter(r, opts.filter),
  );
  const sorted = [...filtered];
  sorted.sort((a, b) => {
    if (opts.sort === "site") return a.siteCode.localeCompare(b.siteCode, "en");
    if (opts.sort === "status") return a.httpStatus.localeCompare(b.httpStatus);
    if (opts.sort === "count") return b.count - a.count;
    return b.bucketUtc.localeCompare(a.bucketUtc);
  });
  return sorted;
}

export function matchesDrillQuery(row: ObservabilityDrillRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [
    row.source,
    row.evidenceSummary,
    row.backupBasename ?? "",
    row.backupSha256 ?? "",
    row.ok ? "موفق" : "ناموفق",
  ]
    .join(" ")
    .toLowerCase()
    .includes(q);
}

export function matchesDrillFilter(row: ObservabilityDrillRow, filter: string): boolean {
  if (filter === "all") return true;
  if (filter === "ok") return row.ok;
  if (filter === "fail") return !row.ok;
  return true;
}

export function filterAndSortDrills(
  rows: ObservabilityDrillRow[],
  opts: { query: string; filter: string; sort: DrillSortKey },
): ObservabilityDrillRow[] {
  const filtered = rows.filter(
    (r) => matchesDrillQuery(r, opts.query) && matchesDrillFilter(r, opts.filter),
  );
  const sorted = [...filtered];
  sorted.sort((a, b) => {
    if (opts.sort === "ok") return Number(b.ok) - Number(a.ok);
    if (opts.sort === "duration") return b.durationMs - a.durationMs;
    return b.recordedAt.localeCompare(a.recordedAt);
  });
  return sorted;
}

export type RelatedObservabilityLink = { id: string; label: string; to: string };

export function relatedObservabilityLinks(): RelatedObservabilityLink[] {
  return [
    { id: "overview", label: "نمای کلی", to: "/" },
    { id: "fleet-drift", label: "ناهماهنگی تنظیمات", to: "/fleet?tab=drift" },
    { id: "audit", label: "سابقه تغییرات", to: "/audit" },
    { id: "bundles", label: "Bundle", to: "/portal-bundles" },
  ];
}
